import React from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Tippy from '@tippyjs/react';
import FormSelect from '../form-select';
import SelectTable from '../select-table';
import { useSaleFormContext } from './SaleFormContext';

function CustomerCard({
    customerCardRef,
    onChangeCustomer,
    inputCustomerValue,
    onInputChangeCustomer,
    customerValue,
    loadCustomerPageOptions,
    onClickFetchTaxPayerId,
    noAfmCredentials,
    taxOfficeOnChange,
    taxOfficeValue,
    countryOnChange,
    countryValue,
    loadTaxOfficePageOptions,
    loadCountriesPageOptions,
    myDataInvoiceType,
}) {
    const { t } = useTranslation();
    const { isFieldRequired, isFieldDisabled, isVisible } = useSaleFormContext();
    const { register, control, getValues, formState: { errors } } = useFormContext();

    return (
        <div className="card customerCard" ref={customerCardRef}>
            <div className="card-header">
                <i className="fa fa-user" style={{ marginRight: 6, color: "#013289" }}></i>
                <span className="prinnerdTab">{t("Customer")}</span>
            </div>
            <div className="card-body" style={{ paddingLeft: 0, paddingRight: 0 }}>
                <div className="row customerRow">

                    <div className={`col-md-3 selectInputContainer form-group customerFromGroup ${isFieldRequired('customer') ? 'required' : ''}`}>
                        <label className="control-label inputlabel">{t('Name')}
                            <Tippy trigger="click" content={<span className="tooltip-skin">{t("saleCustomerInfo")}</span>}>
                                <i className="fa fa-x2 fa-info-circle infoicon"></i>
                            </Tippy>
                        </label>
                        <Controller
                            render={({ field, ref }) => (
                                <SelectTable {...field}
                                    ref={ref}
                                    inputId="customer-react-select"
                                    menuWidth={500}
                                    onChange={onChangeCustomer}
                                    inputValue={inputCustomerValue}
                                    onChangeValue={onChangeCustomer}
                                    onInputChange={onInputChangeCustomer}
                                    createTable={true}
                                    selectedValue={customerValue}
                                    selectedInvoiceType={getValues().invoiceType}
                                    asyncPaginate={true}
                                    columns={[
                                        { header: t("Name"), field: "name", flex: 8, asLabel: true },
                                        { header: t("taxpayerId"), field: "taxpayerId", flex: 4 },
                                    ]}
                                    isDisabled={isFieldDisabled("customer")}
                                    loadOptions={loadCustomerPageOptions}
                                    className={`form-control ${errors.customer ? 'is-invalid' : ''}`}
                                    placeholder={t('placeholderSelectCT')}
                                />
                            )}
                            control={control}
                            rules={{ required: isFieldRequired('customer') }}
                            name="customer"
                            defaultValue=""
                        />
                        <div className="invalid-feedback">{errors.customer?.message}</div>
                    </div>

                    <div id="taxpayerContainer" className={`col-md-2 form-group ${isFieldRequired('taxpayerId') ? 'required' : ''}`}>
                        <label className="control-label inputlabel">{t("taxpayerId")}
                            <Tippy trigger="click" content={<span className="tooltip-skin">{t("searchCustomerInfo")}</span>}>
                                <i className="fa fa-x2 fa-info-circle infoicon"></i>
                            </Tippy>
                        </label>
                        <div className="input-group mb-2">
                            <div className="input-group-prepend">
                                <Tippy disabled={!noAfmCredentials} trigger="mouseenter" content={<span className="tooltip-skin">{t("checkAfmCredentialsRequired")}</span>}>
                                    <span
                                        className={`input-group-text fetchTaxPayerId${noAfmCredentials ? ' disabled' : ''}`}
                                        onClick={noAfmCredentials ? undefined : onClickFetchTaxPayerId}
                                        style={noAfmCredentials ? { opacity: 0.5, cursor: 'not-allowed', pointerEvents: 'all' } : {}}
                                    >
                                        <i className="fa fa-search"></i>
                                    </span>
                                </Tippy>
                            </div>
                            <input
                                maxLength={22}
                                name="taxpayerId"
                                type="text"
                                disabled={isFieldDisabled("taxpayerId")}
                                {...register('taxpayerId', { maxLength: 22, required: isFieldRequired('taxpayerId') })}
                                onKeyDown={(e) => { e.key === 'Enter' && e.preventDefault(); }}
                                className={`form-control ${errors.taxpayerId ? 'is-invalid' : ''}`}
                            />
                            <div className="invalid-feedback">{errors.taxpayerId?.message}</div>
                        </div>
                    </div>

                    <div className="col-md-2 selectInputContainer form-group required">
                        <label className="control-label inputlabel">{t("Country")}</label>
                        <Controller
                            render={({ field, ref }) => (
                                <SelectTable {...field}
                                    key={myDataInvoiceType}
                                    ref={ref}
                                    inputId="country-react-select"
                                    width={500}
                                    onChange={countryOnChange}
                                    onChangeValue={(value) => { field.onChange(value); countryOnChange(value); }}
                                    asyncPaginate={true}
                                    isDisabled={isFieldDisabled("country")}
                                    columns={[{ header: t("Name"), hideHeader:true, field: "alpha2", asLabel: true }]}
                                    loadOptions={loadCountriesPageOptions}
                                    t={t}
                                    myDataInvoiceType={myDataInvoiceType}
                                    className={`form-control ${errors.country ? 'is-invalid' : ''}`}
                                    placeholder={t('placeholderSelectCT')}
                                />
                            )}
                            control={control}
                            rules={{ maxLength: 100 }}
                            name="country"
                            defaultValue=""
                        />

                        <div className="invalid-feedback">{errors.country?.message}</div>

                    </div>

                    <div className={`col-md-2 form-group cityContainer ${isFieldRequired('city') ? 'required' : ''}`}>
                        <label className="control-label inputlabel">{t("City")}</label>
                        <input
                            name="city"
                            {...register('city', { maxLength: 32, required: isFieldRequired("city") })}
                            type="text"
                            maxLength={32}
                            autoComplete="nope"
                            disabled={isFieldDisabled("city")}
                            onKeyDown={(e) => { e.key === 'Enter' && e.preventDefault(); }}
                            className={`form-control ${errors.city ? 'is-invalid' : ''}`}
                        />
                        <div className="invalid-feedback">{errors.city?.message}</div>
                    </div>

                    <div className={`col-md-2 form-group addressContainer ${isFieldRequired('fulladdress') ? 'required' : ''}`}>
                        <label className="control-label inputlabel">{t("Address")}</label>
                        <input
                            name="fulladdress"
                            {...register('fulladdress', { maxLength: 64, required: isFieldRequired("fulladdress") })}
                            onKeyDown={(e) => { e.key === 'Enter' && e.preventDefault(); }}
                            maxLength={64}
                            type="text"
                            autoComplete="nope"
                            disabled={isFieldDisabled("fulladdress")}
                            className={`form-control ${errors.fulladdress ? 'is-invalid' : ''}`}
                        />
                        <div className="invalid-feedback">{errors.fulladdress?.message}</div>
                    </div>

                    <div className={`col-md-2 form-group addressNumberContainer ${isFieldRequired('addressNumber') ? 'required' : ''}`}>
                        <label className="control-label inputlabel">{t("Number")}</label>
                        <input
                            name="addressNumber"
                            {...register('addressNumber', { maxLength: 16, required: isFieldRequired("addressNumber") })}
                            onKeyDown={(e) => { e.key === 'Enter' && e.preventDefault(); }}
                            type="text"
                            autoComplete="nope"
                            disabled={isFieldDisabled("addressNumber")}
                            className={`form-control ${errors.addressNumber ? 'is-invalid' : ''}`}
                        />
                        <div className="invalid-feedback">{errors.addressNumber?.message}</div>
                    </div>

                    <div className={`col-md-1 form-group postalCodeContainer ${isFieldRequired('postalCode') ? 'required' : ''}`}>
                        <label className="control-label inputlabel">{t("PostalCodeShort")}</label>
                        <input
                            name="postalCode"
                            {...register('postalCode', { maxLength: 16, required: isFieldRequired("postalCode") })}
                            onKeyDown={(e) => { e.key === 'Enter' && e.preventDefault(); }}
                            maxLength={16}
                            type="text"
                            autoComplete="nope"
                            disabled={isFieldDisabled("postalCode")}
                            className={`form-control ${errors.postalCode ? 'is-invalid' : ''}`}
                        />
                        <div className="invalid-feedback">{errors.postalCode?.message}</div>
                    </div>

                    <div className="col-md-3 selectInputContainer form-group customerFromGroup">
                        <label className="control-label inputlabel">{t("taxOffice")}</label>
                        <Controller
                            render={({ field, ref }) => (
                                <SelectTable {...field}
                                    ref={ref}
                                    inputId="taxoffice-react-select"
                                    width={500}
                                    onChange={taxOfficeOnChange}
                                    onChangeValue={taxOfficeOnChange}
                                    selectedValue={taxOfficeValue}
                                    asyncPaginate={true}
                                    isDisabled={isFieldDisabled("taxOffice")}
                                    columns={[{ header: t("Name"), field: "name", asLabel: true }]}
                                    loadOptions={loadTaxOfficePageOptions}
                                    className={`form-control ${errors.taxOffice ? 'is-invalid' : ''}`}
                                    placeholder={t('placeholderSelectCT')}
                                />
                            )}
                            control={control}
                            rules={{ maxLength: 100 }}
                            name="taxOffice"
                            defaultValue=""
                        />
                        <div className="invalid-feedback">{errors.customer?.message}</div>
                    </div>

                    <div className="col-md-4 form-group">
                        <label className="control-label inputlabel">{t("subjectField")}</label>
                        <input
                            name="subjectField"
                            maxLength={150}
                            {...register('subjectField', { maxLength: 150 })}
                            type="text"
                            onKeyDown={(e) => { e.key === 'Enter' && e.preventDefault(); }}
                            disabled={isFieldDisabled("subjectField")}
                            className={`form-control ${errors.subjectField ? 'is-invalid' : ''}`}
                        />
                        <div className="invalid-feedback">{errors.subjectField?.message}</div>
                    </div>

                    <div className="col-md-2" id="saveCustomerForFuture" style={{ display: isVisible('saveCustomerForFuture') ? 'unset' : 'none' }}>
                        <label className="control-label">{t("saveCustomerForFuture")}</label>
                        <div className="form-switch">
                            <input className="form-check-input" name="saveCustomerForFuture" {...register('saveCustomerForFuture')} type="checkbox" />
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
}

export default CustomerCard;
